import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { formatPrice, displayColor } from "../utils/formatters";

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, updateCart, removeFromCart, getCartTotal } = useCart();

  const handleQuantityChange = async (item, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(item.product_id, item.size, item.color);
      toast.success("Item removed from cart");
    } else {
      await updateCart(item.product_id, newQuantity, item.size, item.color);
    }
  };

  const handleRemove = async (item) => {
    await removeFromCart(item.product_id, item.size, item.color);
    toast.success("Item removed from cart");
  };

  const handleCheckout = () => {
    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    navigate("/checkout");
  };

  const total = getCartTotal();

  return (
    <div className="min-h-screen pt-24 pb-12 px-6 md:px-12" data-testid="cart-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-mono text-[#FFD700] text-xs tracking-[0.3em] mb-1">YOUR BAG</p>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight uppercase">CART</h1>
          </div>
          {cart.items.length > 0 && (
            <p className="font-mono text-[#A3A3A3] text-sm">{cart.items.reduce((s, i) => s + i.quantity, 0)} item{cart.items.reduce((s, i) => s + i.quantity, 0) !== 1 ? 's' : ''}</p>
          )}
        </div>

        {cart.items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <ShoppingBag className="w-16 h-16 mx-auto text-[#A3A3A3] mb-6" />
            <h2 className="font-heading text-2xl mb-3">YOUR CART IS EMPTY</h2>
            <p className="text-[#A3A3A3] font-mono text-sm tracking-wider mb-8">Ready to move with intention?</p>
            <Link to="/shop" className="btn-primary inline-flex items-center gap-3" data-testid="start-shopping-btn">
              Continue Shopping
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Order Summary - first on mobile, right on desktop */}
            <div className="lg:col-span-4 lg:order-2">
              <div className="border border-[#FFD700]/30 p-6 lg:sticky lg:top-24">
                <h2 className="font-heading text-xl mb-4">ORDER SUMMARY</h2>

                <div className="space-y-3 border-b border-white/10 pb-4 mb-4">
                  <div className="flex justify-between text-[#A3A3A3] text-sm">
                    <span>Subtotal</span>
                    <span className="font-mono">A${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#A3A3A3] text-sm">
                    <span>Shipping</span>
                    <span className="font-mono text-xs">Calculated at checkout</span>
                  </div>
                  {cart.items.reduce((sum, i) => sum + i.quantity, 0) >= 3 && (
                    <p className="font-mono text-xs text-[#FFD700]">3+ items = free shipping!</p>
                  )}
                </div>

                <div className="flex justify-between text-lg mb-4">
                  <span className="font-heading">TOTAL</span>
                  <span className="font-mono text-[#FFD700]" data-testid="cart-total">{formatPrice(total)}</span>
                </div>

                <button
                  onClick={handleCheckout}
                  className="btn-primary w-full flex items-center justify-center gap-3"
                  data-testid="checkout-btn"
                >
                  Checkout
                  <ArrowRight className="w-5 h-5" />
                </button>

                <Link
                  to="/shop"
                  className="block text-center text-[#A3A3A3] hover:text-white mt-4 font-mono text-sm tracking-wider transition-colors"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>

            {/* Cart Items */}
            <div className="lg:col-span-8 lg:order-1 space-y-4">
              {cart.items.map((item, index) => (
                <motion.div
                  key={`${item.product_id}-${item.size}-${item.color}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-6 p-6 border border-white/10 hover:border-[#FFD700]/30 transition-colors"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Image */}
                  <Link to={`/product/${item.product_id}`} className="w-32 h-40 flex-shrink-0">
                    <img
                      src={item.product?.images?.[0]}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Details */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <Link
                        to={`/product/${item.product_id}`}
                        className="font-heading text-xl hover:text-[#FFD700] transition-colors"
                      >
                        {item.product?.name}
                      </Link>
                      <p className="text-[#A3A3A3] text-sm mt-1">
                        Size: {item.size} / Color: {displayColor(item.color)}
                      </p>
                      <p className="font-mono text-[#FFD700] text-lg mt-2">
                        {formatPrice(item.product?.price)}
                      </p>
                    </div>

                    {/* Quantity & Remove */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleQuantityChange(item, item.quantity - 1)}
                          className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-[#FFD700] transition-colors"
                          data-testid={`qty-decrease-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-mono w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item, item.quantity + 1)}
                          className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-[#FFD700] transition-colors"
                          data-testid={`qty-increase-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemove(item)}
                        className="text-[#A3A3A3] hover:text-red-500 transition-colors"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
